'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Input, Spin, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { Feature } from '../types';
import { fetchAllFeatures } from '../utils/submissionApi';

interface FeatureSelectorProps {
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}

// Feature grouping that mirrors the admin dashboard / FeatureGrid categories
const CATEGORY_ORDER = [
  'Amenities',
  'Activities',
  'Accommodation',
  'Facilities',
  'Rules & Access',
  'Other',
];

const CATEGORY_MAP: Record<string, string[]> = {
  Amenities: [
    'Toilets', 'Showers', 'Hot Showers', 'Drinking Water', 'Fresh Water',
    'Power', 'WiFi Internet', 'WiFi', 'Internet Cafe', 'Laundromat',
    'Public Phone', 'Dump Point', 'LPG Swap', 'Ice',
  ],
  Activities: [
    'Fishing', 'Fishing Spot', 'Beach Fishing', 'Swimming', 'Hiking',
    'Boating', 'Boat Ramp', 'Public Boat Ramp', 'Beach', 'Bicycle',
  ],
  Accommodation: [
    'Caravans', 'Caravan Park', 'Caravan Parks', 'Tent Site', 'Camping Area',
    'Campground', 'Camping', 'Cabins', 'Resort', 'Free Camping', 'Showgrounds',
  ],
  Facilities: [
    'BBQ', 'Campfire', 'Shade', 'Parking', 'Picnic Tables',
    "Children's Play Facility", 'Playground',
  ],
  'Rules & Access': [
    'No Pets', 'Pet Friendly', 'Accessible', 'Camping Fee', 'No Camping',
    'No Fires', 'Mobile Phone Coverage',
  ],
};

function categorise(feature: Feature): string {
  const desc = feature.desc || feature.feature_type || '';
  for (const [cat, keywords] of Object.entries(CATEGORY_MAP)) {
    if (keywords.some((k) => k.toLowerCase() === desc.toLowerCase())) return cat;
  }
  return 'Other';
}

const FeatureSelector: React.FC<FeatureSelectorProps> = ({
  selectedIds,
  onChange,
}) => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchAllFeatures()
      .then((f) => setFeatures(f))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Group features by category
  const grouped = useMemo(() => {
    const groups: Record<string, Feature[]> = {};
    for (const cat of CATEGORY_ORDER) groups[cat] = [];

    for (const f of features) {
      const cat = categorise(f);
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(f);
    }

    // Filter by search
    if (search.trim()) {
      const q = search.toLowerCase();
      const filtered: Record<string, Feature[]> = {};
      for (const [cat, list] of Object.entries(groups)) {
        const matches = list.filter(
          (f) =>
            (f.desc || '').toLowerCase().includes(q) ||
            (f.feature_type || '').toLowerCase().includes(q),
        );
        if (matches.length > 0) filtered[cat] = matches;
      }
      return filtered;
    }

    // Remove empty categories
    return Object.fromEntries(
      Object.entries(groups).filter(([, list]) => list.length > 0),
    );
  }, [features, search]);

  const toggle = (id: number) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id],
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spin tip="Loading features..." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Input
        prefix={<SearchOutlined className="text-gray-400" />}
        placeholder="Search features..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
      />

      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedIds.map((id) => {
            const f = features.find((x) => x.id === id);
            return (
              <Tag
                key={id}
                closable
                onClose={() => toggle(id)}
                className="!rounded-full !text-xs"
                color="blue"
              >
                {f?.desc || `Feature #${id}`}
              </Tag>
            );
          })}
        </div>
      )}

      <div className="space-y-5 max-h-[400px] overflow-y-auto pr-1">
        {Object.entries(grouped).map(([category, list]) => (
          <div key={category}>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              {category}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
              {list.map((f) => (
                <Checkbox
                  key={f.id}
                  checked={selectedIds.includes(f.id)}
                  onChange={() => toggle(f.id)}
                  className="!text-sm"
                >
                  {f.desc || f.feature_type}
                </Checkbox>
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-400">
        {selectedIds.length} feature{selectedIds.length !== 1 ? 's' : ''}{' '}
        selected
      </p>
    </div>
  );
};

export default FeatureSelector;
